import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useUpdateUser } from "@/user/api";
import { useUser } from "@/api/api";
import { Spinner } from "@/components/ui/spinner";
import { useEmailManagement } from "@knowsuchagency/django-allauth";

const formSchema = z.object({
  first_name: z.string().min(1, "First name is required"),
  last_name: z.string().min(1, "Last name is required"),
});

interface UserProfileDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export const UserProfileDialog = ({ isOpen, onOpenChange }: UserProfileDialogProps) => {
  // Only render dialog contents when open to avoid unnecessary API calls
  if (!isOpen) {
    return <Dialog open={isOpen} onOpenChange={onOpenChange} />;
  }

  return <UserProfileDialogContent isOpen={isOpen} onOpenChange={onOpenChange} />;
};

const UserProfileDialogContent = ({ isOpen, onOpenChange }: UserProfileDialogProps) => {
  const user = useUser();
  const updateUser = useUpdateUser();

  const {
    emails,
    setPrimary,
    requestVerification,
    addEmail,
    isVerifying,
    isAdding,
  } = useEmailManagement();

  const [newEmail, setNewEmail] = React.useState("");

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      first_name: user.data?.first_name || "",
      last_name: user.data?.last_name || "",
    },
  });

  // Update form values when user data changes
  React.useEffect(() => {
    if (user.data) {
      form.reset({
        first_name: user.data.first_name || "",
        last_name: user.data.last_name || "",
      });
    }
  }, [user.data, form]);

  const isFirstNameEmpty = !user.data?.first_name;
  const isLastNameEmpty = !user.data?.last_name;

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    if (!user.data?.id) return;

    try {
      await updateUser.mutateAsync({
        userId: user.data.id,
        data: {
          first_name: values.first_name,
          last_name: values.last_name,
        },
      });
      onOpenChange(false);
    } catch (error: any) {
      form.setError("root", {
        type: "server",
        message: error?.response?.data?.detail || "Failed to update profile",
      });
    }
  };

  const handleAddEmail = async () => {
    if (!newEmail.trim()) return;

    try {
      await addEmail(newEmail.trim());
      setNewEmail("");
    } catch (error) {
      console.error("Failed to add email:", error);
    }
  };

  const sortedEmails = React.useMemo(() => {
    if (!emails) return [];
    return [...emails].sort((a, b) => {
      if (a.primary) return -1;
      if (b.primary) return 1;
      return 0;
    });
  }, [emails]);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="w-fit">
        <DialogHeader>
          <DialogTitle>Edit Profile</DialogTitle>
          <DialogDescription>Update your account information and contact details</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="first_name"
              render={({ field }) => (
                <FormItem>
                  <label htmlFor="first_name">First Name</label>
                  <FormControl>
                    <Input
                      placeholder="First Name"
                      {...field}
                      className={isFirstNameEmpty ? "ring-2 ring-yellow-500 border-yellow-500" : ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="last_name"
              render={({ field }) => (
                <FormItem>
                  <label htmlFor="last_name">Last Name</label>
                  <FormControl>
                    <Input
                      placeholder="Last Name"
                      {...field}
                      className={isLastNameEmpty ? "ring-2 ring-yellow-500 border-yellow-500" : ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Email Management Section */}
            <div className="space-y-3 pt-2 border-t">
              <label className="text-sm font-medium">Email Addresses</label>
              {sortedEmails.length > 0 ? (
                <div className="space-y-2">
                  {sortedEmails.map((email) => (
                    <div key={email.email} className="space-y-2 p-3 border rounded-md">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium">{email.email}</span>
                        <div className="flex items-center gap-2">
                          {email.primary && (
                            <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded">
                              Primary
                            </span>
                          )}
                          {email.verified ? (
                            <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded">
                              Verified
                            </span>
                          ) : (
                            <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-800 rounded">
                              Unverified
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {!email.primary && email.verified && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setPrimary(email.email)}
                            className="flex-1"
                          >
                            Make Primary
                          </Button>
                        )}
                        {!email.verified && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => requestVerification(email.email)}
                            disabled={isVerifying}
                            className="flex-1"
                          >
                            {isVerifying ? (
                              <Spinner className="h-3 w-3" />
                            ) : (
                              "Resend Verification"
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No email addresses found</p>
              )}

              {/* Add New Email */}
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="Add new email address"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddEmail();
                    }
                  }}
                  disabled={isAdding}
                />
                <Button
                  type="button"
                  onClick={handleAddEmail}
                  disabled={!newEmail.trim() || isAdding}
                  size="sm"
                >
                  {isAdding ? <Spinner className="h-3 w-3" /> : "Add"}
                </Button>
              </div>
            </div>

            {form.formState.errors.root && (
              <p className="text-sm font-medium text-destructive">
                {form.formState.errors.root.message}
              </p>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!form.formState.isValid || updateUser.isPending}
              >
                {updateUser.isPending ? (
                  <Spinner className="h-4 w-4" />
                ) : (
                  "Save"
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
